import { useTranslate } from '@tolgee/react';
import { Select, Input, Form, Button } from 'antd';
import StoreSelector from 'components/StoreSelector';
import { useMemo } from 'react';
import { StoreInfo } from 'services/api/store';
import { AnswerOrigin, QuestionReplayStatus } from './services';
import moment from 'moment';
import { useRequest } from 'ahooks';
import { list } from 'services/api/assistant.account';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

export enum ItemFieldType {
  spuId = 'spuId',
  name = 'name',
}

export interface MercadoFAQFilterValue {
  storeList?: StoreInfo[];
  itemFieldType: ItemFieldType;
  itemFieldValue?: string;
  dateRange?: [moment.Moment, moment.Moment];
  keyword?: string;
  region?: General.UpperCaseRegion;
  follower?: '1' | '0';
  status: QuestionReplayStatusWithAll;
  lang?: General.SystemLanguage;
  answerUserIds?: number[];
  answerOrigins?: AnswerOrigin[];
}

export const initialValues: MercadoFAQFilterValue = {
  itemFieldType: ItemFieldType.name,
  status: 'unanswered',
  dateRange: [moment().subtract(30, 'days'), moment()],
  region: undefined,
  follower: undefined,
  storeList: undefined,
  answerUserIds: [],
  answerOrigins: [],
};

export function useAnswerOriginLabel() {
  const { t } = useTranslate();

  const answerOriginLabelMap = useMemo(() => {
    return {
      MERCADO_QA_ANSWER_ORIGIN_HUMAN: t('手动填写并提交'),
      MERCADO_QA_ANSWER_ORIGIN_AI: t('直接提交 AI 推荐答案'),
      MERCADO_QA_ANSWER_ORIGIN_AI_EDITED: t('编辑后提交 AI 推荐答案'),
      MERCADO_QA_ANSWER_ORIGIN_AI_AUTO: t('AI 自动提交'),
      MERCADO_QA_ANSWER_ORIGIN_EXTERNAL_PLATFORM: t('其他平台回复'),
    };
  }, [t]);

  return {
    t,
    answerOriginLabelMap,
  };
}

export type QuestionReplayStatusWithAll = QuestionReplayStatus | 'all';

export const replayStatusWithAll: QuestionReplayStatusWithAll[] = ['all', 'unanswered', 'answered'];
export interface MercadoFAQFilterProps {
  value: MercadoFAQFilterValue;
  onChange: React.Dispatch<React.SetStateAction<MercadoFAQFilterValue>>;
}

const MercadoFAQFilter = (props: MercadoFAQFilterProps) => {
  const { t, answerOriginLabelMap } = useAnswerOriginLabel();
  const { onChange } = props;
  const [form] = Form.useForm();

  const { whiteListConfig: { MERCADO_QA_LLM } = {} } = useOrgConfigStateValue();

  const itemTypeOptions = useMemo(
    () => [
      {
        label: t('sider.商品名称'),
        value: ItemFieldType.name,
      },
      {
        label: 'spuId',
        value: ItemFieldType.spuId,
      },
    ],
    [t],
  );
  const triggerForm = () => {
    onChange((pre) => {
      return {
        ...pre,
        ...form.getFieldsValue(),
      };
    });
  };

  const { loading, data: options } = useRequest(() => {
    return list({ page: 1, pageSize: 1000 }).then((res) => {
      return res.data?.map(({ id, name, account }) => {
        return {
          value: id,
          label: name || account,
        };
      });
    });
  });

  return (
    <Form
      form={form}
      initialValues={initialValues}
      className="w-full"
      layout="inline"
      onFinish={triggerForm}
    >
      <div className="w-full flex bg-[var(--fett-bg-panel)] px-4 py-3 justify-between">
        <div className="flex flex-wrap gap-y-2">
          <Form.Item name="region" style={{ display: 'none' }}>
            {/* <Input /> */}
          </Form.Item>
          <Form.Item name="follower" style={{ display: 'none' }}>
            {/* <Input /> */}
          </Form.Item>
          <Form.Item name="storeList" style={{ display: 'none' }}>
            {/* <Select mode="multiple" /> */}
          </Form.Item>
          <Form.Item dependencies={['region', 'follower', 'storeList']}>
            {({ getFieldValue, setFieldsValue }) => {
              const storeList = getFieldValue('storeList');
              const region = getFieldValue('region');
              const follower = getFieldValue('follower');
              const hasValue = Boolean(
                region || follower === '1' || (storeList && storeList.length > 0),
              );

              return (
                <StoreSelector
                  scene="BASIC_RECEPTION"
                  platformLimit="MERCADO"
                  quickSelectDisable
                  displayUnreadCount={false}
                  displayTriggerIcon={false}
                  value={{
                    region,
                    follower,
                    storeList,
                  }}
                  onChange={(value) => {
                    const { storeList } = value;
                    setFieldsValue({
                      storeList,
                    });
                  }}
                  displayNodeRender={(displayNode) => {
                    return (
                      <div className="w-48">
                        <Select
                          mode="multiple"
                          showArrow
                          placeholder={t('remind.选择店铺')}
                          value={hasValue ? ['value'] : []}
                          tagRender={() => <>{displayNode}</>}
                          open={false}
                        />
                      </div>
                    );
                  }}
                />
              );
            }}
          </Form.Item>
          <Form.Item dependencies={['itemFieldValue', 'itemFieldType']}>
            {({ getFieldValue }) => {
              const itemFieldType = getFieldValue('itemFieldType');
              return (
                <Form.Item name="itemFieldValue" noStyle>
                  <Input
                    addonBefore={
                      <Form.Item name="itemFieldType" noStyle>
                        <Select options={itemTypeOptions} />
                      </Form.Item>
                    }
                    placeholder={t('department.请输入x', {
                      x: itemFieldType === ItemFieldType.spuId ? 'spuId' : t('sider.商品名称'),
                    })}
                  />
                </Form.Item>
              );
            }}
          </Form.Item>
          <Form.Item name="answerUserIds">
            <Select
              mode="multiple"
              style={{ minWidth: '150px' }}
              loading={loading}
              options={options}
              placeholder={t('选择回复人')}
              maxTagCount={2}
              showArrow
              allowClear
              dropdownMatchSelectWidth={false}
              optionFilterProp="label"
            />
          </Form.Item>
          {MERCADO_QA_LLM && (
            <Form.Item name="answerOrigins">
              <Select
                mode="multiple"
                style={{ minWidth: '150px' }}
                placeholder={t('选择回复来源')}
                showArrow
                allowClear
                dropdownMatchSelectWidth={false}
                optionFilterProp="label"
                options={Object.entries(answerOriginLabelMap).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            </Form.Item>
          )}
          <Form.Item name="keyword">
            <Input placeholder={t('请输入问题和回答关键搜索')} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={form.submit}>
              {t('ViewShops.shop.table.search.btn.text')}
            </Button>
          </Form.Item>
          <Form.Item>
            <Button onClick={() => form.resetFields()}>{t('filter.reset')}</Button>
          </Form.Item>
        </div>
      </div>
    </Form>
  );
};
export default MercadoFAQFilter;
